// src/pages/PodcastRSSMaker.js
import React, { useState } from 'react';

const PodcastRSSMaker = () => {
  const [podcastData, setPodcastData] = useState({
    title: '',
    link: '',
    description: '',
    language: '',
    thumbnail: '', // Will store a data URL for preview (if valid)
    episodes: [],
  });
  const [generatedRSS, setGeneratedRSS] = useState('');
  const [loadRSSInput, setLoadRSSInput] = useState('');
  const [thumbnailError, setThumbnailError] = useState('');

  // Handle podcast details change
  const handlePodcastChange = (e) => {
    setPodcastData({ ...podcastData, [e.target.name]: e.target.value });
  };

  // Handle thumbnail file upload and run validations
  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setThumbnailError('Invalid file type. Only JPEG and PNG images are allowed.');
      setPodcastData({ ...podcastData, thumbnail: '' });
      return;
    }

    // Create an image to check its dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      if (width !== height) {
        setThumbnailError('Thumbnail must be square (width and height must be equal).');
        setPodcastData({ ...podcastData, thumbnail: '' });
        URL.revokeObjectURL(objectUrl);
        return;
      }
      if (width < 1400 || height < 1400) {
        setThumbnailError('Thumbnail dimensions are too small. Minimum size is 1400×1400 pixels.');
        setPodcastData({ ...podcastData, thumbnail: '' });
        URL.revokeObjectURL(objectUrl);
        return;
      }
      if (width > 3000 || height > 3000) {
        setThumbnailError('Thumbnail dimensions are too large. Maximum size is 3000×3000 pixels.');
        setPodcastData({ ...podcastData, thumbnail: '' });
        URL.revokeObjectURL(objectUrl);
        return;
      }

      // If valid, read the file as a data URL for preview.
      const reader = new FileReader();
      reader.onloadend = () => {
        setPodcastData({ ...podcastData, thumbnail: reader.result });
        setThumbnailError('');
      };
      reader.readAsDataURL(file);
      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = () => {
      setThumbnailError('Failed to load image. Please try another file.');
      setPodcastData({ ...podcastData, thumbnail: '' });
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  };

  // Handle changes in an episode’s fields
  const handleEpisodeChange = (index, field, value) => {
    const newEpisodes = [...podcastData.episodes];
    newEpisodes[index] = { ...newEpisodes[index], [field]: value };
    setPodcastData({ ...podcastData, episodes: newEpisodes });
  };

  // Add a new episode entry
  const addEpisode = () => {
    setPodcastData({
      ...podcastData,
      episodes: [
        ...podcastData.episodes,
        { title: '', pubDate: '', description: '', audio: '' },
      ],
    });
  };

  // Delete an episode entry
  const deleteEpisode = (index) => {
    const newEpisodes = podcastData.episodes.filter((_, i) => i !== index);
    setPodcastData({ ...podcastData, episodes: newEpisodes });
  };

  // Generate the RSS feed XML based on the podcast data
  const handleGenerateRSS = () => {
    const currentDate = new Date().toUTCString();
    let itemsXML = '';

    podcastData.episodes.forEach((episode) => {
      if (episode.title && episode.audio) {
        const formattedDate = episode.pubDate
          ? new Date(episode.pubDate).toUTCString()
          : currentDate;
        itemsXML += `
    <item>
      <title><![CDATA[${episode.title}]]></title>
      <pubDate>${formattedDate}</pubDate>
      <description><![CDATA[${episode.description}]]></description>
      <enclosure url="${episode.audio}" type="audio/mpeg" />
    </item>`;
      }
    });

    // Optionally include the thumbnail as an itunes:image element if available
    const thumbnailXML =
      podcastData.thumbnail && !thumbnailError
        ? `<itunes:image href="${podcastData.thumbnail}" />`
        : '';

    const rssXML = `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0" xmlns:itunes="http://www.itunes.com/dtds/podcast-1.0.dtd">
  <channel>
    <title><![CDATA[${podcastData.title}]]></title>
    <link>${podcastData.link}</link>
    <description><![CDATA[${podcastData.description}]]></description>
    <language>${podcastData.language}</language>
    <pubDate>${currentDate}</pubDate>
    ${thumbnailXML}
    ${itemsXML}
  </channel>
</rss>`;

    setGeneratedRSS(rssXML.trim());
  };

  // Load an existing RSS feed and repopulate the form fields
  const handleLoadRSS = () => {
    if (!loadRSSInput.trim()) {
      alert('Please paste your existing RSS feed XML.');
      return;
    }
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(loadRSSInput, 'text/xml');

      if (xmlDoc.querySelector('parsererror')) {
        alert('Error parsing XML. Please check the format.');
        return;
      }

      const channel = xmlDoc.querySelector('channel');
      if (!channel) {
        alert('No channel found in the provided RSS feed.');
        return;
      }

      const title = channel.querySelector('title')?.textContent || '';
      const link = channel.querySelector('link')?.textContent || '';
      const description = channel.querySelector('description')?.textContent || '';
      const language = channel.querySelector('language')?.textContent || '';

      // Load thumbnail from itunes:image if available
      const thumbnail =
        channel.querySelector('itunes\\:image, itunes\\:image')?.getAttribute('href') ||
        '';

      const episodes = [];
      const items = channel.querySelectorAll('item');
      items.forEach((item) => {
        const epTitle = item.querySelector('title')?.textContent || '';
        const epPubDate = item.querySelector('pubDate')?.textContent || '';
        const epDescription = item.querySelector('description')?.textContent || '';
        const enclosure = item.querySelector('enclosure');
        const epAudio = enclosure ? enclosure.getAttribute('url') : '';
        episodes.push({
          title: epTitle,
          pubDate: epPubDate,
          description: epDescription,
          audio: epAudio,
        });
      });

      setPodcastData({
        title,
        link,
        description,
        language,
        thumbnail,
        episodes,
      });
    } catch (e) {
      alert('Error loading RSS feed: ' + e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white p-8 admin-page">
      <h1 className="text-3xl font-bold mb-4">Podcast RSS Feed Maker</h1>
      <form
        className="border border-[#4d4d4d] p-4 rounded mb-8"
        onSubmit={(e) => {
          e.preventDefault();
          handleGenerateRSS();
        }}
      >
        <h2 className="text-2xl mb-4">Podcast Details</h2>
        <div className="mb-4">
          <label className="block mb-1">Podcast Title:</label>
          <input
            type="text"
            name="title"
            value={podcastData.title}
            onChange={handlePodcastChange}
            placeholder="Enter podcast title"
            required
            className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1">Podcast Link:</label>
          <input
            type="text"
            name="link"
            value={podcastData.link}
            onChange={handlePodcastChange}
            placeholder="Enter podcast website link"
            required
            className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1">Podcast Description:</label>
          <textarea
            name="description"
            value={podcastData.description}
            onChange={handlePodcastChange}
            placeholder="Enter podcast description"
            required
            className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1">Language (e.g., en-us):</label>
          <input
            type="text"
            name="language"
            value={podcastData.language}
            onChange={handlePodcastChange}
            placeholder="Enter language code"
            required
            className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
          />
        </div>

        {/* Podcast Thumbnail Upload */}
        <div className="mb-4">
          <label className="block mb-1">Podcast Thumbnail:</label>
          <input
            type="file"
            accept="image/jpeg, image/png"
            onChange={handleThumbnailUpload}
            className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
          />
          {thumbnailError && (
            <p className="text-red-500 mt-2">{thumbnailError}</p>
          )}
          {podcastData.thumbnail && !thumbnailError && (
            <img
              src={podcastData.thumbnail}
              alt="Podcast Thumbnail Preview"
              className="mt-2 w-32 h-32 object-cover rounded"
            />
          )}
        </div>

        {/* Episodes Section */}
        <div className="mb-4">
          <h2 className="text-2xl mb-4">Episodes</h2>
          {podcastData.episodes.map((episode, index) => (
            <div
              key={index}
              className="border border-[#4d4d4d] p-4 rounded mb-4"
            >
              <div className="mb-2">
                <label className="block mb-1">Episode Title:</label>
                <input
                  type="text"
                  value={episode.title}
                  onChange={(e) =>
                    handleEpisodeChange(index, 'title', e.target.value)
                  }
                  placeholder="Enter episode title"
                  required
                  className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
                />
              </div>
              <div className="mb-2">
                <label className="block mb-1">Publication Date:</label>
                <input
                  type="date"
                  value={
                    episode.pubDate
                      ? new Date(episode.pubDate)
                          .toISOString()
                          .split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    handleEpisodeChange(index, 'pubDate', e.target.value)
                  }
                  className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
                />
              </div>
              <div className="mb-2">
                <label className="block mb-1">Episode Description:</label>
                <textarea
                  value={episode.description}
                  onChange={(e) =>
                    handleEpisodeChange(index, 'description', e.target.value)
                  }
                  placeholder="Enter episode description"
                  className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
                />
              </div>
              <div className="mb-2">
                <label className="block mb-1">Audio URL:</label>
                <input
                  type="text"
                  value={episode.audio}
                  onChange={(e) =>
                    handleEpisodeChange(index, 'audio', e.target.value)
                  }
                  placeholder="Enter audio file URL"
                  required
                  className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded"
                />
              </div>
              <button
                type="button"
                onClick={() => deleteEpisode(index)}
                className="text-red-500"
              >
                Delete Episode
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addEpisode}
            className="bg-blue-900 hover:bg-blue-800 p-2 rounded"
          >
            Add Episode
          </button>
        </div>

        <div className="text-center">
          <button type="submit" className="bg-green-700 hover:bg-green-600 p-2 rounded">
            Generate RSS Feed
          </button>
        </div>
      </form>

      {/* Generated RSS Feed Section */}
      <div className="border border-[#4d4d4d] p-4 rounded mb-8">
        <h2 className="text-2xl mb-4">Generated RSS Feed XML</h2>
        <textarea
          value={generatedRSS}
          readOnly
          className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded h-64 font-mono"
        />
      </div>

      {/* Load Existing RSS Feed */}
      <div className="border border-[#4d4d4d] p-4 rounded mb-8">
        <h2 className="text-2xl mb-4">Load Existing RSS Feed</h2>
        <textarea
          value={loadRSSInput}
          onChange={(e) => setLoadRSSInput(e.target.value)}
          placeholder="Paste your existing RSS feed XML here"
          className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded h-64 font-mono"
        />
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={handleLoadRSS}
            className="bg-blue-900 hover:bg-blue-800 p-2 rounded"
          >
            Load RSS Feed
          </button>
        </div>
      </div>
    </div>
  );
};

export default PodcastRSSMaker;